import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Check, LoaderCircle, Settings2 } from "lucide-react";
import { Navigate } from "react-router-dom";
import {
  api,
  type ProWorkspaceType,
  type WorkspaceSettings,
  type WorkspaceSettingsPatch,
} from "./api";
import { useCrm } from "./crm-context";
import { workspaceDefinition } from "./workspace-registry";

const value = (data: FormData, key: string): string => {
  const item = data.get(key);
  return typeof item === "string" ? item.trim() : "";
};
const checked = (data: FormData, key: string): boolean => data.get(key) === "on";
const optional = (input: string): string | null => input || null;

export default function WorkspaceSettingsScreen({
  expected,
}: {
  expected: ProWorkspaceType;
}): ReactNode {
  const { activeOrganization, activeWorkspace } = useCrm();
  const [model, setModel] = useState<WorkspaceSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!activeOrganization || activeWorkspace?.type !== expected) return;
    let alive = true;
    setBusy(true);
    setError(null);
    void api
      .workspaceSettings(activeOrganization.id, expected)
      .then((next) => {
        if (alive) setModel(next);
      })
      .catch((reason: unknown) => {
        if (alive)
          setError(
            reason instanceof Error ? reason.message : "No pudimos cargar la configuración.",
          );
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [activeOrganization, activeWorkspace?.type, expected]);

  if (activeWorkspace && activeWorkspace.type !== expected)
    return <Navigate to={workspaceDefinition(activeWorkspace.type).homeRoute} replace />;
  if (!activeOrganization || !activeWorkspace) return null;
  const organizationId = activeOrganization.id;
  if (!activeWorkspace.capabilities.includes("MANAGE_ORGANIZATION"))
    return (
      <section className="screen">
        <div className="error-panel" role="alert">
          Tu rol no puede modificar la configuración de esta organización.
        </div>
      </section>
    );
  if (busy)
    return (
      <section className="screen">
        <div className="inline-loading">
          <LoaderCircle className="spin" size={22} /> Cargando configuración…
        </div>
      </section>
    );
  if (error || !model)
    return (
      <section className="screen">
        <div className="error-panel" role="alert">
          {error ?? "No encontramos la configuración."}
        </div>
      </section>
    );
  const hasScheduling = ["VETERINARY", "GROOMING", "TRAINING", "CARE"].includes(expected);
  const hasServiceCompletion = ["GROOMING", "TRAINING", "CARE"].includes(expected);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const input: WorkspaceSettingsPatch = {
      publicName: value(data, "publicName"),
      description: optional(value(data, "description")),
      publicProfileEnabled: checked(data, "publicProfileEnabled"),
      contact: {
        email: optional(value(data, "email")),
        phone: optional(value(data, "phone")),
        whatsappE164: optional(value(data, "whatsapp")),
        address: optional(value(data, "address")),
        cityArea: optional(value(data, "cityArea")),
      },
      notifications: {
        appointmentChanges: checked(data, "appointmentChanges"),
        dailySummary: checked(data, "dailySummary"),
      },
      branding: { accentColor: value(data, "accentColor") },
      ...(hasScheduling
        ? {
            scheduling: {
              timezone: value(data, "timezone"),
              defaultServiceDurationMinutes: Number(value(data, "duration")),
              appointmentBufferMinutes: Number(value(data, "buffer")),
            },
          }
        : {}),
      ...(hasServiceCompletion
        ? {
            operational: {
              requireCompletionNotes: checked(data, "requireCompletionNotes"),
              requireServiceMedia: checked(data, "requireServiceMedia"),
            },
          }
        : {}),
    };
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const updated = await api.updateWorkspaceSettings(organizationId, expected, input);
      setModel(updated);
      setSaved(true);
      const accentColor = updated.settings.branding?.accentColor;
      if (accentColor)
        window.dispatchEvent(
          new CustomEvent("tappet-workspace-branding", {
            detail: { organizationId, workspace: expected, accentColor },
          }),
        );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos guardar la configuración.");
    } finally {
      setSaving(false);
    }
  }

  const { organization, settings } = model;
  const title = workspaceDefinition(expected).label;
  return (
    <section className="screen workspace-settings">
      <header className="operational-header">
        <div>
          <span className="eyebrow">ORGANIZACIÓN Y WORKSPACE</span>
          <h1>Configuración de {title}</h1>
          <p>
            Datos públicos y preferencias operativas. Los permisos del equipo se administran por rol
            y no se editan desde aquí.
          </p>
        </div>
        <Settings2 size={30} aria-hidden="true" />
      </header>
      {error && (
        <div className="error-panel" role="alert">
          {error}
        </div>
      )}
      {saved && (
        <div className="success-panel" role="status">
          <Check size={17} />
          Configuración guardada.
        </div>
      )}
      <form
        key={JSON.stringify(model)}
        className="workspace-settings-form"
        onSubmit={(event) => void submit(event)}
      >
        <section className="panel">
          <h2>Presencia pública</h2>
          <div className="settings-grid">
            <Field
              label="Nombre público"
              name="publicName"
              defaultValue={organization.name}
              required
            />
            <Field
              label="Email"
              name="email"
              type="email"
              defaultValue={organization.publicEmail ?? ""}
            />
            <Field label="Teléfono" name="phone" defaultValue={organization.publicPhone ?? ""} />
            <Field
              label="WhatsApp"
              name="whatsapp"
              defaultValue={organization.whatsappE164 ?? ""}
            />
            <Field
              label="Dirección"
              name="address"
              defaultValue={organization.publicAddress ?? ""}
            />
            <Field
              label="Ciudad o zona"
              name="cityArea"
              defaultValue={organization.cityArea ?? ""}
            />
            <label className="field field-wide">
              Descripción
              <textarea name="description" rows={3} defaultValue={organization.description ?? ""} />
            </label>
            <label className="toggle field-wide">
              <input
                name="publicProfileEnabled"
                type="checkbox"
                defaultChecked={organization.publicProfileEnabled}
              />
              <span>Habilitar perfil público de la organización</span>
            </label>
          </div>
        </section>
        <section className="panel">
          <h2>{hasScheduling ? "Agenda y operación" : "Preferencias del workspace"}</h2>
          <div className="settings-grid">
            {hasScheduling && (
              <>
                <Field
                  label="Zona horaria"
                  name="timezone"
                  defaultValue={settings.scheduling?.timezone ?? "America/Argentina/Buenos_Aires"}
                  required
                />
                <Field
                  label="Duración sugerida de servicio (min)"
                  name="duration"
                  type="number"
                  min="15"
                  max="480"
                  defaultValue={String(settings.scheduling?.defaultServiceDurationMinutes ?? 60)}
                  required
                />
                <Field
                  label="Margen entre turnos (min)"
                  name="buffer"
                  type="number"
                  min="0"
                  max="180"
                  defaultValue={String(settings.scheduling?.appointmentBufferMinutes ?? 0)}
                  required
                />
              </>
            )}
            <Field
              label="Color del workspace"
              name="accentColor"
              pattern="#[0-9a-fA-F]{6}"
              defaultValue={settings.branding?.accentColor ?? workspaceDefinition(expected).accent}
              required
            />
            <label className="toggle">
              <input
                name="appointmentChanges"
                type="checkbox"
                defaultChecked={
                  hasScheduling && (settings.notifications?.appointmentChanges ?? true)
                }
                disabled={!hasScheduling}
              />
              <span>
                {hasScheduling
                  ? "Avisar cambios de turnos en la app (push si el destinatario lo habilitó)"
                  : "Avisos de turnos — no disponibles en este workspace"}
              </span>
            </label>
            <label className="toggle">
              <input name="dailySummary" type="checkbox" defaultChecked={false} disabled />
              <span>Resumen operativo diario — no disponible: sin canal operativo</span>
            </label>
            {hasServiceCompletion && (
              <>
                <label className="toggle">
                  <input
                    name="requireCompletionNotes"
                    type="checkbox"
                    defaultChecked={settings.operational?.requireCompletionNotes ?? false}
                  />
                  <span>Solicitar observaciones al finalizar</span>
                </label>
                <label className="toggle">
                  <input
                    name="requireServiceMedia"
                    type="checkbox"
                    defaultChecked={settings.operational?.requireServiceMedia ?? false}
                  />
                  <span>Exigir al menos un archivo privado al finalizar el servicio</span>
                </label>
              </>
            )}
          </div>
        </section>
        {hasScheduling && (
          <section className="panel settings-service-note">
            <h2>Servicios y sedes</h2>
            <p>
              El catálogo, la duración concreta de cada servicio y las sedes se administran desde
              sus módulos con los permisos correspondientes. Esta pantalla guarda los valores
              predeterminados del workspace.
            </p>
          </section>
        )}
        <div className="form-actions">
          <button className="button button-primary" disabled={saving}>
            {saving ? "Guardando…" : "Guardar configuración"}
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({
  label,
  name,
  defaultValue,
  type = "text",
  required,
  min,
  max,
  pattern,
}: {
  label: string;
  name: string;
  defaultValue: string;
  type?: string;
  required?: boolean;
  min?: string;
  max?: string;
  pattern?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        name={name}
        type={type}
        defaultValue={defaultValue}
        required={required}
        min={min}
        max={max}
        pattern={pattern}
      />
    </label>
  );
}
